import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { NavParams, ToastController, ViewController } from "ionic-angular";
import { Observable } from "rxjs/Observable";
import { Subscription } from "rxjs/Subscription";
import { CatalogData } from "../../../../providers/catalog";
import { ClientData } from "../../../../providers/client";
import { CouponsData } from "../../../../providers/coupons";
import { GenFormBuilder } from "../../../../providers/form";
import { ReserveData } from "../../../../providers/reserve";
import { Utilities } from "../../../../providers/utilities";
import { ICatalog } from "../../../../shared/models/catalog.model";
import { IClient } from "../../../../shared/models/client.model";
import { IProfileUsers } from "../../../../shared/models/profile-users.model";
import * as sharedSelectors from "../../../../shared/reducers/shared.reducer";
import { TicketProvider } from "./../../../../providers/ticket";

@Component({
  selector: "app-coupons-form",
  templateUrl: "coupons-form.html",
})
export class CouponsFormComponent implements OnInit, OnDestroy {

  public get isNew() {
    return !this.form.get("id").value;
  }

  public form: FormGroup;
  public clientSubscribe: Subscription;
  public submitAttempt: boolean = false;
  public clientId: string;
  public defaultLanguage: string;
  public language: string;
  public languageMenu: "en" | "es" = "es";
  public client: IClient;
  public coupon: any;
  public products$: Observable<ICatalog[]>;
  public reserves$: Observable<any[]>;
  public tickets$: Observable<any[]>;
  public profiles$: Observable<IProfileUsers[]>;
  public optionsFroala = this.utilities.FroalaKey;

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<{}>,
    private genForm: GenFormBuilder,
    private couponsData: CouponsData,
    private catalogData: CatalogData,
    private ticketProvider: TicketProvider,
    private clientData: ClientData,
    private reserveData: ReserveData,
    private toastController: ToastController,
    public viewCtrl: ViewController,
    public utilities: Utilities,
    private navParams: NavParams,
  ) { }

  public ngOnInit() {
    this.clientId = this.navParams.get("clientId");
    this.coupon = this.navParams.get("coupon");
    this.clientSubscribe = this.store.select(sharedSelectors.getClient).subscribe((client) => {
      if (client) {
        this.client = client;
        this.defaultLanguage = client.defaultLanguage;
        this.language = this.defaultLanguage;
        this.buildForm();
        this.getProducts();
        this.getReserves();
        this.getProfiles();
        this.getTickets();
      }
    });
  }

  public onFormSubmitted({ value, valid }: { value: any; valid: boolean }) {
    this.submitAttempt = true;
    if (valid) {
      const load = this.utilities.setLoading();
      // Si se ingreso un cupon manualmente se debe verificar que sea valido
      if (!value.id && value.code) {
        value.code = value.code.toUpperCase();
        if (value.code.length < 5) {
          return this.utilities.Toast.create({
            message: "The code entered must contain at least 5 characters",
          }).present().then(() => load.dismiss());
        }

        this.couponsData.getCouponsByCode(this.clientId, value.code)
          .then((codes) => {
            if (codes) {
              this.toastController.create({
                duration: 3000,
                message: "The code entered is already registered",
              }).present().then(() => load.dismiss());
            } else {
              this.createOrUpdate(value, load);
            }
          });
      } else {
        this.createOrUpdate(value, load);
      }

    }
  }

  public selectCatalog(event) {
    if (event.result) {
      this.form.get("objects").setValue(event.result);
    }
  }

  public selectReserve(event) {
    if (event.result) {
      this.form.get("objects").setValue(event.result);
    }
  }

  public selectTicket(event) {
    if (event.result) {
      this.form.get("objects").setValue(event.result);
    }
  }
  public selectProfile(event) {
    if (event.result) {
      this.form.get("profiles_view").setValue(event.result);
    }
  }

  public ngOnDestroy() {
    if (this.clientSubscribe) { this.clientSubscribe.unsubscribe(); }
  }

  private getTickets() {
    this.tickets$ = this.ticketProvider.getTicketList(this.client.id);
  }

  private getProducts() {
    this.products$ = this.catalogData.getCatalogActiveByClientId(this.client.id);
  }

  private getReserves() {
    this.reserves$ = this.reserveData.getReservesByClientId(this.client.id);
  }

  private getProfiles() {
    this.profiles$ = this.clientData.getClientActiveProfiles(this.client.id);
  }

  private buildForm() {
    this.form = this.formBuilder.group({});
    this.genForm.genInputString("id", this.form);
    this.genForm.genToggle("autogenerate", this.form, false);
    this.genForm.genInputLang("title", this.form, this.defaultLanguage);
    this.genForm.genInputLang("description", this.form, this.defaultLanguage);
    this.genForm.genInputLang("name", this.form, this.defaultLanguage);
    this.genForm.genInputLang("terms", this.form, this.defaultLanguage);
    this.genForm.genInputString("image", this.form, true);
    this.genForm.genInputString("background_color", this.form, true);
    this.genForm.genInputString("code", this.form);
    this.genForm.genInputString("type_code", this.form, true, "qr");
    this.genForm.genInputString("type_coupon", this.form, true);
    this.genForm.genInputString("type_discount", this.form, true);
    this.genForm.genInputString("privacy", this.form, true, "public");
    this.genForm.genToggle("redeemed", this.form, false, false);
    this.genForm.genInputString("profiles_view", this.form);
    this.genForm.genInputJSON("objects", this.form, true);
    this.genForm.genInputNumber("objects_value", this.form, true);
    this.genForm.genInputString("objects_type_pay", this.form, true, "app");
    this.genForm.genInputString("expiration_date", this.form, true);
    this.genForm.genInputString("quota", this.form, true);
    this.genForm.genToggle("state", this.form);

    this.form.get("quota").setValidators([Validators.min(1)]);
    this.form.get("objects_value").setValidators([Validators.max(100), Validators.min(1)]);
    this.form.get("quota").updateValueAndValidity();
    this.form.get("objects_value").updateValueAndValidity();

    this.inspectFormValues();
    this.patchForm();
  }

  private createOrUpdate(value, load) {
    this.couponsData.createCoupon(this.clientId, value)
      .then(() => {
        this.toastController.create({
          duration: 3000,
          message: "The coupon was succesfully created",
        }).present();
        this.viewCtrl.dismiss().then(() => load.dismiss());
      });
  }

  private patchForm() {
    if (this.coupon) {
      this.form.patchValue(this.coupon);
    }
  }

  private inspectFormValues() {
    this.form.get("privacy").valueChanges.subscribe((data) => {
      if (data === "private") {
        this.form.get("profiles_view").setValidators([Validators.required]);
      } else {
        this.form.get("profiles_view").clearValidators();
      }

      this.form.get("profiles_view").updateValueAndValidity();
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/coupons/components/coupons-form/coupons-form.ts